import React from "react";

import { cx, css } from "@emotion/css";

import { useStyles } from "@/hooks/useStyles.ts";
import { Slider, type SliderMark } from "@/uiKit/atoms/inputs/Slider.tsx";

export type LibreviewGroupedInclusionTBRFilterProps = {
  value: number;
  onValueChange: (value: number) => void;
  className?: string;
};

// Power-scale mapping for the TBR slider — two independent tuning knobs:
//   offset — shifts the low end; larger values soften how much the smallest values spread out.
//   scale  — curvature/steepness; >1 gives more room to small (low-TBR) values, 1 ≈ linear.
const offset = 0.5;
const scale = 20;
const forward = (value: number) => Math.pow(value + offset, 1 / scale);
const inverse = (position: number) => Math.round(Math.pow(position, scale) - offset);

const marks: SliderMark[] = [];
const visibleMarks = [0, 4, 30];
for (let i = 0; i <= 30; i += 1) {
  if (visibleMarks.includes(i)) {
    marks.push({ value: forward(i), label: i });
  } else {
    marks.push({ value: forward(i) });
  }
}

export const LibreviewGroupedInclusionTBRFilter: React.FC<LibreviewGroupedInclusionTBRFilterProps> = ({
  value,
  onValueChange,
  className,
}) => {
  const styles = useStyles(makeStyles);

  return (
    <Slider
      id="sync-grouped-inclusion-tbr-filter"
      min={forward(0)}
      max={forward(30)}
      step={null}
      value={forward(value)}
      track="inverted"
      scale={inverse}
      marks={marks}
      onChange={position => {
        const newValue = inverse(position);
        onValueChange(newValue);
      }}
      valueLabelDisplay="auto"
      className={cx(styles.slider, className)}
    />
  );
};

// Hide the tick dots (.MuiSlider-mark) but keep the value captions (.MuiSlider-markLabel).
const makeStyles = () => ({
  slider: css({
    "& .MuiSlider-mark": {
      display: "none",
    },
  }),
});
